import React, { useState } from 'react';
import { X, Mail, MessageSquare, Check, Loader2, Plus, Trash2, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SendQuoteModal = ({ quote, lead, onClose, onSuccess }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendVia, setSendVia] = useState(['sms', 'email']);
  const [clientInfo, setClientInfo] = useState({
    name: lead?.name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
  });
  const [depositPercent, setDepositPercent] = useState(50);
  const [slots, setSlots] = useState([]); // [{date, time}]
  const [error, setError] = useState('');

  const addSlot = () => setSlots([...slots, { date: '', time: '09:00' }]);
  const updateSlot = (i, field, value) => {
    const copy = [...slots];
    copy[i] = { ...copy[i], [field]: value };
    setSlots(copy);
  };
  const removeSlot = (i) => setSlots(slots.filter((_, idx) => idx !== i));

  const handleSend = async () => {
    if (!clientInfo.name.trim()) return setError('Client name is required');
    if (sendVia.includes('email') && !clientInfo.email) return setError('Email is required to send via email');
    if (sendVia.includes('sms') && !clientInfo.phone) return setError('Phone is required to send via SMS');
    if (sendVia.length === 0) return setError('Please select at least one send method');

    const dp = Number(depositPercent);
    if (Number.isNaN(dp) || dp < 0 || dp > 100) return setError('Deposit % must be between 0 and 100');

    setError('');
    setSending(true);

    try {
      // 1. Configure portal (deposit % + availability slots)
      if (quote?.id) {
        const cleanSlots = slots
          .filter((s) => s.date && s.time)
          .map((s) => ({ date: s.date, time: s.time }));
        await axios.post(
          `${API}/client-portal/quote/${quote.id}/portal-config`,
          { deposit_percent: dp, availability_slots: cleanSlots },
          { withCredentials: true }
        );
      }

      // 2. Send the quote (backend uses the configured portal token)
      const response = await fetch(`${API}/send-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          quote_id: quote.id,
          lead_id: lead.id,
          client_name: clientInfo.name,
          client_email: clientInfo.email || null,
          client_phone: clientInfo.phone || null,
          send_via: sendVia,
          attach_pdf: false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send quote');
      }

      const data = await response.json();
      setSent(true);
      setTimeout(() => {
        onSuccess?.(data);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Send error:', err);
      setError(err.message || 'Failed to send quote');
    } finally {
      setSending(false);
    }
  };

  const toggleSendMethod = (method) => {
    setSendVia(sendVia.includes(method) ? sendVia.filter((m) => m !== method) : [...sendVia, method]);
  };

  if (sent) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-8 text-center" data-testid="send-quote-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Quote Sent!</h3>
          <p className="text-gray-600">
            Sent via {sendVia.map((m) => m.toUpperCase()).join(' + ')} to {clientInfo.name}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="send-quote-modal">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-gray-900">Send Quote</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={sending} data-testid="send-quote-close">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Quote preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">QUOTE PREVIEW</p>
                <h3 className="text-xl font-bold text-gray-900 mt-1">{quote?.project_name || 'Project Quote'}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {quote?.square_feet} sq ft • {quote?.project_type?.replace('-', ' ')}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">TOTAL</p>
                <p className="text-3xl font-bold text-blue-600">${quote?.total?.toLocaleString() || '0'}</p>
              </div>
            </div>
          </div>

          {/* Client info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Client Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
              <input
                type="text"
                value={clientInfo.name}
                onChange={(e) => setClientInfo({ ...clientInfo, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="John Doe"
                disabled={sending}
                data-testid="send-quote-client-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address {sendVia.includes('email') && '*'}
              </label>
              <input
                type="email"
                value={clientInfo.email}
                onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="john@example.com"
                disabled={sending}
                data-testid="send-quote-client-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number {sendVia.includes('sms') && '*'}
              </label>
              <input
                type="tel"
                value={clientInfo.phone}
                onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+1 (555) 123-4567"
                disabled={sending}
                data-testid="send-quote-client-phone"
              />
            </div>
          </div>

          {/* Portal settings */}
          <div className="space-y-4 border-t pt-5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Approval Portal Settings</h3>
            </div>
            <p className="text-xs text-gray-500 -mt-3">
              Your client will get a secure link where they can sign, schedule, and pay a deposit.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Deposit % *</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={depositPercent}
                  onChange={(e) => setDepositPercent(e.target.value)}
                  className="w-28 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={sending}
                  data-testid="send-quote-deposit-percent"
                />
                <span className="text-gray-600 text-sm">
                  ≈ ${(((quote?.total || 0) * Number(depositPercent || 0)) / 100).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} due at booking
                </span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Availability slots (optional)</label>
                <button
                  type="button"
                  onClick={addSlot}
                  disabled={sending}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium"
                  data-testid="send-quote-add-slot"
                >
                  <Plus className="w-4 h-4" /> Add slot
                </button>
              </div>
              {slots.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Leave empty to let the client suggest a date/time.
                </p>
              ) : (
                <div className="space-y-2">
                  {slots.map((s, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`send-quote-slot-${i}`}>
                      <input
                        type="date"
                        value={s.date}
                        onChange={(e) => updateSlot(i, 'date', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        disabled={sending}
                      />
                      <input
                        type="time"
                        value={s.time}
                        onChange={(e) => updateSlot(i, 'time', e.target.value)}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                        disabled={sending}
                      />
                      <button
                        type="button"
                        onClick={() => removeSlot(i)}
                        disabled={sending}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Send method */}
          <div className="space-y-3 border-t pt-5">
            <h3 className="font-semibold text-gray-900">Send Via</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => toggleSendMethod('email')}
                disabled={sending}
                className={`flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg font-medium transition-colors ${
                  sendVia.includes('email')
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
                data-testid="send-quote-email-toggle"
              >
                <Mail className="h-5 w-5" /> Email
                {sendVia.includes('email') && <Check className="h-4 w-4 ml-auto" />}
              </button>
              <button
                type="button"
                onClick={() => toggleSendMethod('sms')}
                disabled={sending}
                className={`flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg font-medium transition-colors ${
                  sendVia.includes('sms')
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
                data-testid="send-quote-sms-toggle"
              >
                <MessageSquare className="h-5 w-5" /> SMS
                {sendVia.includes('sms') && <Check className="h-4 w-4 ml-auto" />}
              </button>
            </div>
            <p className="text-sm text-gray-500">
              {sendVia.length === 2
                ? 'Sending via both SMS + Email for maximum reach'
                : sendVia.length === 1
                ? `Sending via ${sendVia[0].toUpperCase()} only`
                : 'Select at least one method'}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="send-quote-error">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              disabled={sending}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              data-testid="send-quote-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || sendVia.length === 0}
              className="flex-1 bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="send-quote-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <MessageSquare className="h-5 w-5" /> SEND QUOTE
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendQuoteModal;
